import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";

// The session report's four sections, and the bar that navigates them.
//
// Moved out of SessionSummary.js unchanged so a second surface — the admin
// prompt console — can mount the teacher's own bar rather than declaring a
// lookalike. SessionSummary keeps the accordion, the stored-report fetch and
// the report modal; only the bar is shared.
export const SESSION_REPORT_TABS = [
  { key: 'summary', label: 'Session Summary' },
  { key: 'students', label: 'Student Detail' },
  { key: 'questions', label: 'Question Detail' },
  { key: 'transcript', label: 'Transcript' },
];

export const normalizeReportTab = (tab) => {
  if (!tab) return null;
  const normalized = String(tab).toLowerCase().trim();
  if (normalized === "safety-concerns" || normalized === "safety_concerns" || normalized === "incidents") {
    return "incidents";
  }
  if (normalized === "summary" || normalized === "session-summary") {
    return "summary";
  }
  if (normalized === "students" || normalized === "student-detail" || normalized === "student_detail") {
    return "students";
  }
  if (normalized === "questions" || normalized === "question-detail" || normalized === "question_detail") {
    return "questions";
  }
  if (normalized === "transcript") {
    return "transcript";
  }
  return null;
};

const TabNavBar = styled.div`
  display: flex;
  align-items: stretch;
  height: 70px;
  background: #F9FAFD;
  border-bottom: 1px solid #EEECE7;
  padding: 0 24px;
`;

const TabNavItem = styled.button`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;

  img {
    width: 24px;
    height: 24px;
  }
  background: none;
  border: none;
  border-bottom: 3px solid ${({ $active }) => $active ? '#605DEC' : 'transparent'};
  margin-bottom: -1px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${({ $active }) => $active ? '#605DEC' : '#002E19'};
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
  &:hover { color: #605DEC; }
`;

const MoreTabsBtn = styled.button`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  background: none;
  border: none;
  border-bottom: 3px solid ${({ $active }) => $active ? '#605DEC' : 'transparent'};
  margin-bottom: -1px;
  cursor: pointer;
  color: ${({ $active }) => $active ? '#605DEC' : '#002E19'};
  position: relative;
  &:hover { color: #605DEC; }
`;

const MoreTabsMenu = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  background: #fff;
  border: 1px solid #EEECE7;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(14, 26, 49, 0.12);
  z-index: 200;
  min-width: 160px;
  padding: 4px 0;
`;

const MoreTabsItem = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 10px 16px;
  text-align: left;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? 700 : 400};
  color: ${({ $active }) => $active ? '#605DEC' : '#002E19'};
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #F9FAFD; }
`;

export const ResponsiveTabNav = ({ activeKey, onSelect, tabs = SESSION_REPORT_TABS, idPrefix = "session", ariaLabel = "Session report sections" }) => {
  const navRef = useRef(null);
  const moreRef = useRef(null);
  const widthsRef = useRef(null);
  const [overflowFrom, setOverflowFrom] = useState(tabs.length);
  const [menuOpen, setMenuOpen] = useState(false);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const measure = () => {
      if (!widthsRef.current) {
        const items = nav.querySelectorAll('[data-tab-key]');
        if (items.length === tabs.length) {
          const measured = Array.from(items).map(el => el.offsetWidth);
          if (measured.every(w => w > 0)) widthsRef.current = measured;
        }
      }
      const widths = widthsRef.current;
      if (!widths) return;
      const total = widths.reduce((s, w) => s + w, 0);
      const available = nav.clientWidth;
      if (total <= available) { setOverflowFrom(tabs.length); return; }
      const MORE_W = 44;
      let used = 0;
      let count = 0;
      for (const w of widths) {
        if (used + w > available - MORE_W) break;
        used += w;
        count++;
      }
      setOverflowFrom(count);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const hasOverflow = overflowFrom < tabs.length;
  const visibleTabs = tabs.slice(0, overflowFrom);
  const hiddenTabs = tabs.slice(overflowFrom);
  const activeIsHidden = hasOverflow && hiddenTabs.some(t => t.key === activeKey);

  // Roving tabindex: exactly one tab sits in the tab order. Prefer the active
  // tab; if it's tucked into the overflow menu, fall back to the first visible
  // tab so the tablist stays keyboard-reachable.
  const focusKey = visibleTabs.some(t => t.key === activeKey)
    ? activeKey
    : visibleTabs[0]?.key;

  // WAI-ARIA tabs pattern: Left/Right wrap through the visible tabs (activating
  // as they go), Home/End jump to the ends.
  const onTabKeyDown = (e) => {
    const keys = visibleTabs.map(t => t.key);
    if (keys.length === 0) return;
    const current = keys.indexOf(activeKey);
    let next;
    if (e.key === 'ArrowRight') next = (current + 1 + keys.length) % keys.length;
    else if (e.key === 'ArrowLeft') next = (current - 1 + keys.length) % keys.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = keys.length - 1;
    else return;
    e.preventDefault();
    const nextKey = keys[next];
    onSelect(nextKey);
    navRef.current?.querySelector(`[data-tab-key="${nextKey}"]`)?.focus();
  };

  return (
    <TabNavBar
      ref={navRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onTabKeyDown}
    >
      {visibleTabs.map(tab => {
        const selected = tab.key === activeKey;
        return (
          <TabNavItem
            key={tab.key}
            type="button"
            data-tab-key={tab.key}
            id={`${idPrefix}-tab-${tab.key}`}
            role="tab"
            aria-selected={selected}
            aria-controls={`${idPrefix}-tabpanel-${tab.key}`}
            tabIndex={tab.key === focusKey ? 0 : -1}
            $active={selected}
            onClick={() => onSelect(tab.key)}
          >
            {tab.icon && <img src={tab.icon} alt="" aria-hidden="true" />}
            {tab.label}
          </TabNavItem>
        );
      })}
      {hasOverflow && (
        <MoreTabsBtn
          ref={moreRef}
          type="button"
          aria-label="More report sections"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          $active={activeIsHidden}
          onClick={() => setMenuOpen(o => !o)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 12H20M4 6H20M4 18H20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {menuOpen && (
            <MoreTabsMenu>
              {hiddenTabs.map(tab => (
                <MoreTabsItem
                  key={tab.key}
                  type="button"
                  role="tab"
                  id={`${idPrefix}-tab-${tab.key}`}
                  aria-controls={`${idPrefix}-tabpanel-${tab.key}`}
                  aria-selected={tab.key === activeKey}
                  $active={tab.key === activeKey}
                  onClick={(e) => { e.stopPropagation(); onSelect(tab.key); setMenuOpen(false); }}
                >
                  {tab.label}
                </MoreTabsItem>
              ))}
            </MoreTabsMenu>
          )}
        </MoreTabsBtn>
      )}
    </TabNavBar>
  );
};
